import React, { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  ThemeProvider,
  createTheme,
  styled,
  type Theme,
} from '@mui/material/styles';
import createFurtherTheme from '../../theme';

const Page = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(4),
}));

const Container = styled(Box)(({ theme }: { theme: Theme }) => ({
  maxWidth: 600,
  width: '100%',
  textAlign: 'center',
  padding: theme.spacing(3),
  boxSizing: 'border-box',
}));

const Logo = styled('img')(({ theme }: { theme: Theme }) => ({
  height: '40px',
  marginBottom: theme.spacing(4),
  objectFit: 'contain',
}));

const Title = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontWeight: 400,
  marginBottom: theme.spacing(4),
}));

const Description = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginBottom: theme.spacing(3),
}));

type CenteredProp = { centered?: boolean };

const InstructionsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'centered',
})<CenteredProp>(
  ({ theme, centered }: { theme: Theme; centered?: boolean }) => ({
    textAlign: centered ? 'center' : 'left',
    marginBottom: theme.spacing(3),
  }),
);

const InstructionsTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
}));

const InstructionsList = styled('ol', {
  shouldForwardProp: (prop) => prop !== 'centered',
})<CenteredProp>(
  ({ theme, centered }: { theme: Theme; centered?: boolean }) => ({
    display: centered ? 'inline-block' : 'block',
    textAlign: 'left',
    marginLeft: centered ? 0 : theme.spacing(3),
    paddingLeft: theme.spacing(2),
    listStyleType: 'decimal',
    listStyle: 'decimal outside',
    fontFamily: theme.typography.fontFamily,
    '& li': {
      display: 'list-item',
      listStyle: 'decimal outside',
      marginBottom: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontFamily: theme.typography.fontFamily,
    },
  }),
);

export type ApplicationLoadErrorProps = {
  logoPath: string;
  isAdminSite?: boolean;
  title: string;
  description: string;
  instructionsTitle: string;
  instructions: Array<string>;
  centerInstructions?: boolean;
  action: React.ReactNode;
};

const FONT_STYLE_ID = 'local-storage-error-fonts';

// Required for investor site: fonts are loaded via CSS imports in React components,
// which haven't loaded yet when this error component renders (before app initialization).
// Admin site doesn't need this as fonts are loaded in index.html <head>.
const FONT_DEFINITIONS = `
  @font-face {
    font-family: 'MaisonNeueNew';
    src: url('/assets/fonts/MaisonNeue-Book.woff2') format('woff2'),
         url('/assets/fonts/MaisonNeue-Book.woff') format('woff'),
         url('/assets/fonts/MaisonNeue-Book.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
  }
  @font-face {
    font-family: 'MaisonNeueNew';
    src: url('/assets/fonts/MaisonNeue-Bold.woff2') format('woff2'),
         url('/assets/fonts/MaisonNeue-Bold.woff') format('woff'),
         url('/assets/fonts/MaisonNeue-Bold.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
  }
`;

const ApplicationLoadError: React.FC<ApplicationLoadErrorProps> = ({
  logoPath,
  isAdminSite = false,
  title,
  description,
  instructionsTitle,
  instructions,
  centerInstructions = false,
  action,
}) => {
  const theme = useMemo(
    () =>
      createTheme(
        createFurtherTheme({
          isAdminSite,
          isSubdomain: false,
        }),
      ),
    [isAdminSite],
  );

  useEffect(() => {
    if (isAdminSite || document.getElementById(FONT_STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = FONT_STYLE_ID;
    style.textContent = FONT_DEFINITIONS;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(FONT_STYLE_ID);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [isAdminSite]);

  return (
    <ThemeProvider theme={theme}>
      <Page>
        <Container>
          <Logo src={logoPath} alt="Further logo" />

          <Title variant="h4">{title}</Title>

          <Description variant="body1" color="text.secondary">
            {description}
          </Description>

          <InstructionsContainer centered={centerInstructions}>
            <InstructionsTitle variant="body2">
              {instructionsTitle}
            </InstructionsTitle>
            <InstructionsList centered={centerInstructions}>
              {instructions.map((instruction) => (
                <li key={instruction}>
                  <Typography variant="body2">{instruction}</Typography>
                </li>
              ))}
            </InstructionsList>
          </InstructionsContainer>

          {action}
        </Container>
      </Page>
    </ThemeProvider>
  );
};

export default ApplicationLoadError;
